import request from '@/utils/request';
import * as types from '@/api/repo/types';
import { reqCatch, reqCatchV2, type catchRt } from '@/utils/catch';
import type { AxiosResponse } from 'axios';
import { type errorType } from '@/api/types/common';

// 创建一个项目，创建CR仓库时，请求Headers中，x-zone和x-cell为必填参数
export function createRepo(data: types.createRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: '/api/v2/projects',
    method: 'post',
    data
  });
}
// 判断是否可以创建公开项目
export function createPublicProject(data: types.createRepoResType): Promise<types.createRepoResType> {
  return request({
    url: `/api/v1/global/setting?key=${data.key}`,
    method: 'get',
    data
  });
}

/**
 * 根据模板创建项目
 * @param data
 * @returns
 */
export function createRepoByTpl(data: types.createRepoByTpl): Promise<types.commonRepoResType> {
  const { project_id, ...rest } = data;
  return request({
    url: `/api/v2/projects/${project_id}/template/generate`,
    method: 'post',
    data: rest
  });
}

// 获取创建时的默认分支
export function getCreateDefaultBranch(namespace: string, type: string): Promise<any> {
  return request({
    url: `/api/v2/projects/${namespace}/default_create_setting/${type}`,
    method: 'get'
  });
}

/**
 * 判断importUrl是否reachable
 */
export function repoReachable(data: { import_url: string, import_user_info?: string, import_type?: string; }): catchRt<{ reachable: string, desc: string, authority_web_url: string; }> {
  return reqCatchV2(() =>
    request({
      url: '/api/v2/projects/reachable',
      method: 'post',
      data
    })
  );
}
// 获取一个项目
export function getRepo(params: types.commonRepoReqType, config?: object): Promise<types.commonRepoResType> {
  return reqCatch(
    () =>
      request(
        {
          url: `/api/v2/projects/${params.repoId}?view=all`,
          method: 'get',
          params: {
            statistics: params?.statistics
          }
        },
        config
      ),
    params
  );
}

// 项目详情-带权限-返回审核 audit_fields 字段，仅在项目设置页调用
export function getRepoWithAudit(params: types.commonRepoReqType, config?: object): Promise<types.commonRepoResType> {
  return reqCatch(
    () =>
      request(
        {
          url: `/api/v2/projects/${params.repoId}/audit_info?view=all`,
          method: 'get',
          params: {
            statistics: params?.statistics
          }
        },
        config
      ),
    params
  );
}
// 获取项目详情
export function getRepoDetail(params: types.commonRepoReqType, config?: object): Promise<types.commonRepoResType> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}`,
        method: 'get',
        params
      }, config),
    params
  );
}
// 获取项目详情2
export function getRepoDetailSimple(params: types.commonRepoReqType, config?: object): Promise<types.commonRepoResType> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/simple`,
        method: 'get'
      }, config)
  );
}
// 修改一个项目
export function editRepoSimple(data: Partial<types.commonRepoResType> & { repoId: string | number; }): Promise<any> {
  const { repoId, ...conf } = data;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${repoId}`,
        method: 'put',
        data: conf
      }),
    data
  );
}
// 修改一个项目有udate权限才行
export function editRepo(data: Partial<types.commonRepoResType> & { repoId: string | number; }): Promise<any> {
  const { repoId, ...conf } = data;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${repoId}/risk`,
        method: 'put',
        data: conf
      }),
    data
  );
}
// 删除一个项目
export function deleteRepo(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}`,
        method: 'delete',
        params
      }),
    params
  );
}
// 项目转移 与接口命名保持一致
export function transferRepo(data: { projectId: string; namespace: string; }): Promise<any> {
  const { projectId, namespace } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${projectId}/transfer`,
      method: 'put',
      data: { namespace }
    })
  );
}
/** 获取项目分支 */
export function getRepoBranches(params: {
  project_id: string | number;
  branch_type?: 'not_protect' | '';
  per_page?: number;
  view?: string;
  search?: string;
}): Promise<any> {
  return reqCatch(() =>
    request(
      {
        url: `/api/v2/projects/${params.project_id}/repository/branches`,
        method: 'get',
        params
      },
      { customError: true }
    )
  );
}
/** 获取项目保护分支 */
export function getRepoProtectedBranches(params: { project_id: string | number; }): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/protected_branches`,
      method: 'get',
      params
    })
  );
}
/** 新增项目保护分支 */
export function createProtectedBranches(data: {
  project_id: string | number;
  conf: Record<string, any>;
}): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.project_id}/batch/protected_branches`,
        method: 'post',
        data: data.conf
      }),
    data
  );
}
// 获取能推送的白名单成员
export function getPushWhiteList(params: {
  project_id: string | number | null;
  page: number;
  per_page: number;
  query: string;
}): Promise<any> {
  const { project_id } = params;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${project_id}/permission_resources/code/members`,
        method: 'get',
        params: {
          ...params
        }
      }),
    params
  );
}
// 获取能pr的白名单成员
export function getMergeWhiteList(params: {
  project_id: string | number | null;
  page: number;
  per_page: number;
  query: string;
}): Promise<any> {
  const { project_id } = params;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${project_id}/permission_resources/pr/members`,
        method: 'get',
        params: {
          ...params
        }
      }),
    params
  );
}
/** 编辑项目保护分支  */
export function editProtectedBranches(data: { project_id: string | number; conf: Record<string, any>; }): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.project_id}/repository/branches/protect`,
        method: 'put',
        data: data.conf
      }),
    data
  );
}
/** 删除项目保护分支 */
export function delProtectedBranches(data: { project_id: string | number; branch_name: string; }): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.project_id}/protected_branches/${data.branch_name}`,
        method: 'delete'
      }),
    data
  );
}
// 创建分支
export function createBranch(data: { repoId: string | number; ref: string; branch: string; }): Promise<any> {
  return request({
    url: `/api/v2/projects/${data.repoId}/repository/branches`,
    method: 'post',
    data
  });
}
/** 获取保护分支信息 */
export function getProtectedBranchInfo(params: { project_id: string | number; branch_name: string; }): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.project_id}/protected_branches/${params.branch_name}`
      }),
    params
  );
}
/** 获取单个分支详情 */
export function getBranchDetail(
  params: { project_id: string | number; branch_name: string; },
  customConfig: { customError: boolean; } = { customError: false }
): Promise<any> {
  return request({
    url: `/api/v2/projects/${params.project_id}/repository/branches/${params.branch_name}`
  }, {
    customError: customConfig.customError
  });
}
/** 获取项目保护tag */
export function getProtectedTags(params: { project_id: string | number; }): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/protected_tags`,
      params: {
        page: 1,
        per_page: 100,
        ...params
      }
    })
  );
}
/** 创建项目保护tag */
export function createProtectedTags(data: {
  project_id: string | number;
  name: string;
  create_access_level: number | string;
  roles_can : string[]
}): Promise<any> {
  const { project_id, ...conf } = data;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${project_id}/protected_tags`,
        method: 'post',
        data: conf
      }),
    {}
  );
}
/** 编辑项目保护tag权限 */
export function editProjectTags(data: {
  project_id: string | number;
  name: string;
  create_access_level: number | string;
  roles_can?: string[];
}): Promise<any> {
  const { project_id, ...conf } = data;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${project_id}/protected_tags`,
        method: 'put',
        data: conf
      }),
    {}
  );
}
/** 删除项目保护分支tag权限 */
export function delProjectTags(data: { project_id: string | number; name: string; }) {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.project_id}/protected_tags/${data.name}`,
        method: 'delete'
      }),
    {}
  );
}
/** 获取用户组织列表 */
export function getGroupMembers(): Promise<any> {
  return reqCatch(() =>
    request({
      url: 'api/v2/groups/list',
      params: {
        page: 1,
        per_page: 50,
        develop_mode: 'normal',
        maintained: true
      }
    })
  );
}

/** 获取可创建ai的个人账户和组织列表 */
export function getAiTransferableGroupMembers(): Promise<any> {
  return reqCatch(() =>
    request({
      url: 'api/v2/groups/user_groups/manageable_transfer',
      params: {
        page: 1,
        per_page: 50
      }
    })
  );
}
/** 项目设置模块开发 */
export function getSettingsValues(data: { repoId: string | number; }): Promise<any> {
  const { repoId } = data;
  return reqCatch(() =>
    request(
      {
        url: `/api/v2/projects/${repoId}/module/setting`,
        method: 'get',
        params: data
      },
      {
        customError: true
      }
    )
  );
}
/** 更新项目设置模块开关 */
export function updateSettingsValues(data: {
  repoId: string | number;
  modules: { key: string; value: string | number; type: string; }[];
}): Promise<any> {
  const { repoId, modules } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/module/setting`,
      method: 'put',
      data: { modules }
    })
  );
}
/** 修改项目设置模块开关 */
export function changeSettingsValues(data: {
  repoId: string | number;
  modules: { key: string; value: string | number; type: string; };
}): Promise<any> {
  const { repoId, modules } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/module_item/setting`,
      method: 'put',
      data: modules
    })
  );
}
/** 项目设置仓库分支权限设置 */
export function getRepoBranchSetting(data: { project_id: string; }) {
  const { project_id } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/policies/general`
    })
  );
}
/** 修改项目设置仓库配置 */
export function editRepoBranchSetting(data: { project_id: string; conf: Record<string, any>; }) {
  const { project_id, conf } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/policies/general`,
      method: 'put',
      data: conf
    })
  );
}
/* 项目webhooks相关开始 */
/** 获取项目分支webhook */
export function getRepoWebhooks(params: { repoId: string | null; conf: Record<string, any>; }) {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/hooks`,
        params: params.conf
      }),
    params
  );
}
/** 新增webhook配置 */
export function createWebhook(data: { repoId: string | number | null; conf: Record<string, any>; }) {
  return reqCatch(
    () =>
      request(
        {
          url: `/api/v2/projects/${data.repoId}/hooks`,
          data: data.conf,
          method: 'post'
        },
        {
          customError: true
        }
      ),
    data
  );
}
/** 编辑webhook */
export function editWebhook(data: { repoId: string | null; hook_id: string; conf: Record<string, any>; }) {
  const { repoId, hook_id, conf } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/hooks/${hook_id}`,
      method: 'put',
      data: conf
    })
  );
}
/** 获取webhook详情 */
export function getWebhookDetail(data: { repoId: string | null; hook_id: string; }) {
  const { repoId, hook_id } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/hooks/${hook_id}`
    })
  );
}
/** 获取webhook请求日志 */
export function getWebhookLogs(data: { repoId: string | null; hook_id: string; page?: number; per_page?: number; created_after?: string, created_before?: string }) {
  const { repoId, hook_id, ...param } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/hooks/${hook_id}/logs`,
      params: Object.assign({ page: 1, per_page: 10 }, param)
    })
  );
}
/** 获取项目webhook日志详情 */
export function getWebhookLogDetail(data: { repoId: string | null; hook_id: string; id: string; }) {
  const { repoId, hook_id, id } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/hooks/${hook_id}/logs/${id}`
    })
  );
}
/** 删除webhook */
export function deleteWebHook(data: { repoId: string | null; hook_id: string; }) {
  const { repoId, hook_id } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/hooks/${hook_id}`,
      method: 'delete'
    })
  );
}

/** 重新发送 webhook 请求 */
export function retryWebhook(data: { hook_type: string; resource_id: string; log_id: string | number }) {
  return reqCatch(() =>
    request({
      url: '/api/v2/projects/hooks/retry',
      method: 'post',
      data
    })
  );
}
/* 项目相关webhooks结束 */
// 获取开发者分支权限设置
export function repoPolicies(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/policies/general`,
    method: 'get',
    params
  });
}
// 开发者分支创建权限设置、开发者Tag创建权限设置、分支名规则设置、Tag名规则设置
export function editRepoPolicies(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${data.repoId}/policies/general`,
    method: 'put',
    data
  });
}
// 获取合并请求设置
export function getMergeSetting(params: types.commonRepoReqType, config?: object): Promise<any> {
  const { repoId } = params;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/merge_requests/setting`,
      method: 'get',
      params
    }, config)
  );
}

// 合并请求设置
export function editMergeSetting(data: {
  repoId: string;
  conf: Record<string, any>;
}): Promise<types.commonRepoResType> {
  const { repoId, conf } = data;
  return request({
    url: `/api/v2/projects/${repoId}/merge_requests/setting`,
    method: 'put',
    data: conf
  });
}
// 获取经过身份验证的用户的可见项目列表
export function getRepos(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects`,
        method: 'get',
        params
      }),
    params
  );
}
// 取消收藏一个项目
export function unstarRepo(data: types.commonRepoReqType): Promise<AxiosResponse<{ star_count: number; }>> {
  return request({
    url: `/api/v2/projects/${data.repoId}/unstar`,
    method: 'post',
    data
  });
}
// 收藏一个项目
export function starRepo(data: types.commonRepoReqType): Promise<AxiosResponse<{ star_count: number; }>> {
  return request({
    url: `/api/v2/projects/${data.repoId}/star`,
    method: 'post',
    data
  });
}
// 取消归档项目
export function unarchiveRepo(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.repoId}/unarchive`,
        method: 'post',
        data
      }),
    data
  );
}
// 归档项目
export function archiveRepo(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${data.repoId}/archive`,
        method: 'post',
        data
      }),
    data
  );
}
// Fork一个项目
export function createForkRepo(data: types.forkRepoReqType): Promise<types.forkRepoReqType> {
  return request({
    url: `/api/v2/projects/${data.repoId}/fork`,
    method: 'post',
    data
  });
}
// 删除Fork项目
export function deleteForkRepo(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/fork`,
        method: 'delete',
        params
      }),
    params
  );
}
// 获取项目中的语言
export function getRepoLanguages(params: types.commonRepoReqType): Promise<any> {
  return request(
    {
      url: `/api/v2/projects/${params.repoId}/languages`,
      method: 'get',
      params
    },
    {
      customError: true
    }
  );
}
// 获取项目审计日志列表
export function getRepoAuditLogs(params: {
  repoId: string | number;
  param: {
    page?: number;
    per_page?: number;
    search?: string;
    log_type?: string;
    start_date?: string;
    end_date?: string;
    operator_id?: string;
  };
}): Promise<any> {
  const { repoId, param } = params;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${repoId}/audit_logs`,
        method: 'get',
        params: Object.assign({ page: 1, per_page: 10 }, param)
      }),
    params
  );
}
// 获取项目的Fork列表
export function getRepoForks(params: types.forkRepoReqType): Promise<types.forkRepoReqType> {
  return request({
    url: `/api/v2/projects`,
    method: 'get',
    params
  });
}

/**
 * 获取项目的Fork列表
 */
export function getRepoForkList(params: {
  repoId: string;
  page: number;
  per_page: number;
  /** 是否展示我fork的项目 */
  membership?: boolean;
}): catchRt<{
  page_num: number;
  page_size: number;
  page_count: number;
  total: number;
  content: types.forkRepoReqType[];
}> {
  return reqCatchV2(() => request({
    url: `/api/v2/projects/${params.repoId}/forks`,
    method: 'get',
    params
  }));
}

// 获取项目的Star列表
export function getRepoStars(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/star`,
    method: 'get',
    params
  });
}
// 获取项目Star用户列表
export function getRepoStarUsers(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/star_users`,
    method: 'get',
    params
  });
}
// 检查的项目名称
export function checkRepoName(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: '/api/v2/projects/check_name',
    method: 'get',
    params
  });
}

// 获取代码压缩包/release下载压缩包，仓库某目录下载压缩包
export function getPreArchive(project_id: string, params?: types.PreArchiveReqType) {
  return request({
    url: `/api/v2/projects/${project_id}/repository/pre_archive`,
    method: 'get',
    timeout: 60 * 1000,
    params
  });
}

export function activateMergeRequest(params: {
  project_id: string;
  merge_request_iid: string;
}, data: any): Promise<any> {
  return request({
    url: `/issuepr/api/v1/projects/${params.project_id}/merge_requests/activate/${params.merge_request_iid}`,
    method: 'put',
    data
  });
}

/**
 * 仓库访问统计
 * @param params 项目id
 */
export function repoVisitStatistics(params: {
  /** 项目id */
  projectId: number;
  /** 限制天数 Default value : 30 */
  limit?: number;
}): Promise<AxiosResponse<{
  /** 日期 */
  pdate: string;
  /** 项目id */
  repo_id: string;
  /** 页面浏览量 */
  pv_dl_cnt: number;
  /** 独立访客量 */
  uv_dl_cnt: number;
}[], errorType>> {
  const { projectId, ...rest } = params;
  return request({
    url: `/api/v2/projects/${projectId}/repository/visit_statistics_limit`,
    method: 'get',
    params: rest
  });
}

/**
 * 仓库下载统计
 * @param repoId 项目id
 */
export function repoDownloadStatistics(params: {
  /** 项目id */
  projectId: number;
  /** 限制天数 Default value : 30 */
  limit?: number;
}): Promise<AxiosResponse<{
  /** 日期 */
  pdate: string;
  /** 项目id */
  repo_id: string;
  /** 日统计 */
  today_dl_cnt: number;
  /** 总统计 */
  total_dl_cnt: number;
}[], errorType>> {
  const { projectId, ...rest } = params;
  return request({
    url: `/api/v2/projects/${projectId}/repository/download_statistics_limit`,
    method: 'get',
    params: rest
  });
}

/* 为issue设置自定义字段 */
export function setIssueExtendField(params: {
  project_id: string;
  issue_iid: number;
  data: any;
}): Promise<any> {
  return request({
    url: `/issuepr/api/v1/issue/${params.project_id}/issue-extend/${params.issue_iid}/issue_extend_field`,
    method: 'put',
    data: params.data
  });
}
